import React, { useEffect, useMemo } from 'react';
import { Polyline, CircleMarker, Tooltip, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import type {
  TrackingResponse,
  GeoPoint,
  TrackingTaskLocation,
  TrackingSession,
  TrackingSegment,
} from '../../types/tracking';
import { getRouteColor, geoPointsToLatLngs, calculateBearing } from '../../utils/mapHelpers';
import { formatTime } from '../../utils/dateHelpers';
import { TaskMarker } from './TaskMarker';

/** Screen coordinates + segment id passed up when a segment is right-clicked. */
export interface SegmentContextEvent {
  segmentId: number;
  /** True when there is a next segment in the same session — the backend
   *  requires the extend pair to share a check-in, and each session on
   *  the client maps 1-to-1 to a check-in. */
  extendable: boolean;
  x: number;
  y: number;
}

interface RouteViewProps {
  selectedIds: Set<string>;
  trackingData: Map<string, TrackingResponse>;
  playbackPosition: [number, number] | null;
  /** When true, per-segment polylines listen for `contextmenu` and bubble
   *  the event up via `onSegmentContext`. When false the polylines are
   *  purely visual (viewer / report roles never see the menu). */
  canEditSegments?: boolean;
  onSegmentContext?: (event: SegmentContextEvent) => void;
  /** Segment id whose OSRM path is currently under review; that segment
   *  is drawn muted so the preview overlay is easy to compare against. */
  previewSegmentId?: number | null;
  /** OSRM `[lng, lat]` pairs to draw on top of the raw route. */
  previewPath?: [number, number][] | null;
  /**
   * Master switch for OSRM overlays (applied emerald / pending dashed
   * cyan / preview overlay). When false the map falls back to the
   * plain raw polyline no matter what the server returned — used in
   * envs where the OSRM backend isn't deployed yet.
   */
  osrmFeatureEnabled?: boolean;
}

interface SessionRoute {
  userId: string;
  sessionIndex: number;
  sessionLabel: string;
  color: string;
  segments: TrackingSegment[];
  /** Chronologically sorted points across every segment in the session
   *  — kept for the arrow markers and per-point dots (which look at the
   *  whole route, not one segment). */
  points: GeoPoint[];
  latLngs: [number, number][];
  tasks: TrackingTaskLocation[];
  checkinPoint: GeoPoint | null;
  checkinTime: string | null;
  checkoutPoint: GeoPoint | null;
  checkoutTime: string | null;
}

function createArrowIcon(bearing: number, color: string) {
  return L.divIcon({
    className: '',
    html: `<div style="
      transform:rotate(${bearing}deg);
      color:${color};font-size:16px;font-weight:bold;
      text-shadow:0 0 3px #fff;
    ">&#9654;</div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
}

function createPlaybackIcon() {
  return L.divIcon({
    className: '',
    html: `<div style="
      width:18px;height:18px;border-radius:50%;
      background:#2563eb;border:3px solid #fff;
      box-shadow:0 2px 6px rgba(0,0,0,0.4);
    "></div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });
}

function createCheckinIcon(sessionLabel: string, color: string) {
  return L.divIcon({
    className: '',
    html: `<div style="
      width:28px;height:28px;border-radius:50%;
      background:${color};color:#fff;
      display:flex;align-items:center;justify-content:center;
      font-size:11px;font-weight:700;
      border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);
    ">${sessionLabel}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

function createCheckoutIcon(color: string) {
  return L.divIcon({
    className: '',
    html: `<div style="
      width:28px;height:28px;border-radius:50%;
      background:${color};color:#fff;
      display:flex;align-items:center;justify-content:center;
      font-size:14px;font-weight:700;
      border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);
      opacity:0.7;
    ">&#9632;</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

export const RouteView: React.FC<RouteViewProps> = ({
  selectedIds,
  trackingData,
  playbackPosition,
  canEditSegments = false,
  onSegmentContext,
  previewSegmentId = null,
  previewPath = null,
  osrmFeatureEnabled = false,
}) => {
  const map = useMap();

  // Build one route per session (not per user). Segments are surfaced
  // separately so we can draw one polyline per segment for right-click.
  const sessionRoutes = useMemo(() => {
    const result: SessionRoute[] = [];
    let colorIdx = 0;

    for (const userId of selectedIds) {
      const data = trackingData.get(userId);
      if (!data) continue;

      const totalSessions = data.sessions.length;

      data.sessions.forEach((session: TrackingSession, sIdx: number) => {
        const points = session.geo_points
          .slice()
          .sort((a, b) => new Date(a.recorded_at).getTime() - new Date(b.recorded_at).getTime());

        const color = getRouteColor(colorIdx);
        const sessionLabel = `S${sIdx + 1}`;

        const checkinPoint = points.length > 0 ? points[0] : null;
        const checkoutPoint = points.length > 0 ? points[points.length - 1] : null;

        result.push({
          userId,
          sessionIndex: sIdx,
          sessionLabel: totalSessions > 1 ? sessionLabel : '',
          color,
          segments: session.segments,
          points,
          latLngs: geoPointsToLatLngs(points),
          tasks: session.tasks,
          checkinPoint,
          checkinTime: session.checkin_time,
          checkoutPoint,
          checkoutTime: session.checkout_time,
        });
        colorIdx++;
      });
    }
    return result;
  }, [selectedIds, trackingData]);

  // Auto-fit bounds when routes load
  useEffect(() => {
    const allLatLngs = sessionRoutes.flatMap(r => r.latLngs);
    if (allLatLngs.length > 0) {
      const bounds = L.latLngBounds(allLatLngs);
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
    }
  }, [sessionRoutes, map]);

  return (
    <>
      {sessionRoutes.map(route => {
        const key = `${route.userId}-s${route.sessionIndex}`;
        return (
          <React.Fragment key={key}>
            {/* One polyline per segment — each carries its own right-click
                handler so the reviewer can request OSRM correction. When
                the reviewer has the editor role we also draw a wider,
                fully-transparent "hit" polyline on top of the visible one
                so a right-click within a few pixels of the line still
                registers (a 3-pixel stroke is a very small target).

                Three rendering modes based on the segment's OSRM state:
                  - applied  → emerald OSRM path only (raw hidden — the
                               correction is the source of truth now)
                  - pending  → dashed cyan OSRM path only (raw hidden — a
                               computed correction speaks for itself; the
                               raw drift would just be noise until the
                               reviewer decides)
                  - none     → raw path only
                In every mode the hit target follows whatever the reviewer
                actually sees on the map, so right-click lands where they
                expect it to. */}
            {route.segments.map((segment) => {
              const rawLatLngs = geoPointsToLatLngs(segment.points);
              if (rawLatLngs.length < 2) return null;
              const isPreviewing =
                previewSegmentId !== null && segment.id === previewSegmentId;
              // When the OSRM feature isn't enabled in this environment,
              // ignore any correction fields the server may have returned
              // and fall back to the plain raw polyline. Prevents the map
              // from advertising a workflow that isn't available.
              const isApplied =
                osrmFeatureEnabled &&
                segment.is_osrm_applied === true &&
                !!segment.osrm_path;
              const isPending =
                osrmFeatureEnabled &&
                segment.is_osrm_applied === null &&
                !!segment.osrm_path;
              const hasOsrm = isApplied || isPending;
              const isNotEditable = segment.id === null;
              const wireContextmenu =
                canEditSegments && !isNotEditable && !!onSegmentContext;

              // Pre-computed OSRM `[lat, lng]` polyline (Leaflet order).
              // Used for both the visible overlay and — whenever a
              // correction exists — the hit-target path.
              const osrmLatLngs =
                segment.osrm_path
                  ? segment.osrm_path.map(
                      ([lng, lat]) => [lat, lng] as [number, number],
                    )
                  : null;
              const hitLatLngs = hasOsrm && osrmLatLngs ? osrmLatLngs : rawLatLngs;

              return (
                <React.Fragment key={`seg-${key}-${segment.index}`}>
                  {/* Raw stroke — hidden whenever any OSRM path exists
                      (applied OR pending). The corrected path is the
                      only version we want the reviewer looking at while
                      they decide. */}
                  {!hasOsrm && (
                    <Polyline
                      positions={rawLatLngs}
                      pathOptions={{
                        color: route.color,
                        weight: 3,
                        opacity: isPreviewing ? 0.35 : 0.8,
                        // Prevent Leaflet from bubbling the mouse event up
                        // to the map container, which otherwise fires the
                        // map's default contextmenu handler and swallows
                        // ours.
                        bubblingMouseEvents: false,
                      }}
                      interactive={!wireContextmenu}
                    />
                  )}

                  {/* Applied OSRM path — the corrected route in emerald.
                      Renders in place of the raw polyline. */}
                  {isApplied && osrmLatLngs && (
                    <Polyline
                      positions={osrmLatLngs}
                      pathOptions={{
                        color: '#059669',
                        weight: 4,
                        opacity: 0.9,
                        bubblingMouseEvents: false,
                      }}
                      interactive={!wireContextmenu}
                    />
                  )}

                  {/* Pending correction — dashed cyan line in place of
                      the raw path. Reviewer will Apply or Reject. */}
                  {isPending && osrmLatLngs && (
                    <Polyline
                      positions={osrmLatLngs}
                      pathOptions={{
                        color: '#0891b2',
                        weight: 3,
                        opacity: 0.9,
                        dashArray: '6, 4',
                        bubblingMouseEvents: false,
                      }}
                      interactive={!wireContextmenu}
                    />
                  )}

                  {/* Wide invisible hit target — only rendered when the
                      reviewer can act. 20px stroke; `opacity: 0.001` is
                      visually zero but still hittable (SVG's `opacity: 0`
                      can be optimised out and stop firing events on some
                      renderers). The target follows the visible path so
                      right-click lands where the reviewer expects it. */}
                  {wireContextmenu && (
                    <Polyline
                      positions={hitLatLngs}
                      pathOptions={{
                        color: '#000',
                        weight: 20,
                        opacity: 0.001,
                        // Cursor hints that a context menu is available.
                        className: 'osrm-segment-hit',
                        bubblingMouseEvents: false,
                      }}
                      eventHandlers={{
                        contextmenu: (evt) => {
                          const domEvent = (evt as L.LeafletMouseEvent).originalEvent;
                          L.DomEvent.preventDefault(domEvent);
                          L.DomEvent.stopPropagation(domEvent);
                          onSegmentContext!({
                            segmentId: segment.id as number,
                            // Extendable when a next segment exists in the
                            // same session (backend limits extend to same
                            // check-in — one session ↔ one check-in on the
                            // client).
                            extendable:
                              segment.index < route.segments.length - 1,
                            x: domEvent.clientX,
                            y: domEvent.clientY,
                          });
                        },
                      }}
                    />
                  )}
                </React.Fragment>
              );
            })}

            {/* Preview overlay for the OSRM-corrected path. Rendered
                after the raw polylines so it sits on top. */}
            {previewPath && previewSegmentId !== null &&
              route.segments.some((s) => s.id === previewSegmentId) && (
                <Polyline
                  positions={previewPath.map(([lng, lat]) => [lat, lng] as [number, number])}
                  pathOptions={{
                    color: '#0891b2',
                    weight: 4,
                    opacity: 0.95,
                    dashArray: '6, 4',
                  }}
                />
              )}

            {/* Direction arrows at intervals */}
            {route.points.length > 1 &&
              route.points
                .filter((_, i) => i > 0 && i % Math.max(1, Math.floor(route.points.length / 10)) === 0)
                .map((pt, i) => {
                  const prevIdx = route.points.indexOf(pt) - 1;
                  const prev = route.points[prevIdx];
                  const bearing = calculateBearing(
                    prev.latitude, prev.longitude,
                    pt.latitude, pt.longitude
                  );
                  return (
                    <Marker
                      key={`arrow-${key}-${i}`}
                      position={[pt.latitude, pt.longitude]}
                      icon={createArrowIcon(bearing, route.color)}
                      interactive={false}
                    />
                  );
                })}

            {/* Geo point dots with timestamp tooltip */}
            {route.points.map((pt, i) => (
              <CircleMarker
                key={`pt-${key}-${i}`}
                center={[pt.latitude, pt.longitude]}
                radius={3}
                pathOptions={{
                  color: route.color,
                  fillColor: route.color,
                  fillOpacity: 0.7,
                  weight: 1,
                }}
              >
                <Tooltip direction="top" offset={[0, -6]}>
                  {route.sessionLabel && <strong>{route.sessionLabel} &middot; </strong>}
                  {formatTime(pt.recorded_at)}
                </Tooltip>
              </CircleMarker>
            ))}

            {/* Check-in marker */}
            {route.checkinPoint && (
              <Marker
                position={[route.checkinPoint.latitude, route.checkinPoint.longitude]}
                icon={createCheckinIcon(route.sessionLabel || '▶', route.color)}
                zIndexOffset={500}
              >
                <Tooltip direction="top" offset={[0, -14]} permanent={false}>
                  <strong>Check-in{route.sessionLabel ? ` (${route.sessionLabel})` : ''}</strong>
                  <br />
                  {route.checkinTime ? formatTime(route.checkinTime) : ''}
                </Tooltip>
              </Marker>
            )}

            {/* Check-out marker */}
            {route.checkoutPoint && route.checkoutTime && (
              <Marker
                position={[route.checkoutPoint.latitude, route.checkoutPoint.longitude]}
                icon={createCheckoutIcon(route.color)}
                zIndexOffset={500}
              >
                <Tooltip direction="top" offset={[0, -14]} permanent={false}>
                  <strong>Check-out{route.sessionLabel ? ` (${route.sessionLabel})` : ''}</strong>
                  <br />
                  {formatTime(route.checkoutTime)}
                </Tooltip>
              </Marker>
            )}

            {/* Task markers */}
            {route.tasks.map((taskLoc, i) => (
              <TaskMarker key={`task-${key}-${i}`} taskLocation={taskLoc} />
            ))}
          </React.Fragment>
        );
      })}

      {/* Playback animated marker */}
      {playbackPosition && (
        <Marker
          position={playbackPosition}
          icon={createPlaybackIcon()}
          zIndexOffset={1000}
        />
      )}
    </>
  );
};
